import { Route, RouteProps, useHistory } from 'react-router-dom';
import { useAuth } from '~/contexts/AuthProvider';
import * as Sentry from '@sentry/react';
import constVars from '~/constants/constVars';
import React, { useEffect } from 'react';
import { Center, Loader } from '@qred/components-library';
import { accessToken, TOKEN } from '~/store/apis/utils/accessToken';
import posthog from 'posthog-js';
import { useAccount } from '~/hooks/useAccount';
import { useDispatch } from 'react-redux';
import { toggleGlobalErrorOn } from '~/store/slices/globalError.slice';
import { GlobalErrorType } from '~/enums';

interface AuthenticatedRouteProps extends RouteProps {}

const AuthenticatedRoute = (props: AuthenticatedRouteProps) => {
  const { isAuthenticated, isLoading, isLoggingOut } = useAuth();
  const { setMarket, init, isInitialized, hasNoCompany } = useAccount();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (
      !isLoading &&
      !isAuthenticated &&
      !isLoggingOut &&
      import.meta.env.MODE !== 'internal-prod'
    ) {
      if (window.location.pathname !== constVars.ROUTE_DEFAULT) {
        sessionStorage.setItem(
          'returnTo',
          `${window.location.pathname}${window.location.search}`
        );
      }

      history.push(`/${constVars.ROUTE_LOGIN}`);
    }
  }, [isLoading, isAuthenticated, isLoggingOut]);

  useEffect(() => {
    if (isAuthenticated) {
      accessToken.getDecoded().then((decodedToken: any) => {
        const _market = decodedToken[TOKEN.MARKET];
        if (_market) {
          setMarket(_market);
        }

        const userCode = decodedToken[TOKEN.USER_CODE];

        if (userCode) {
          posthog.identify(userCode);
          Sentry.setUser({ id: userCode });
        }
      });

      init();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Logged in but without access to any company: show the existing
    // "no account" popup instead of rendering pages that need a company.
    if (hasNoCompany) {
      dispatch(toggleGlobalErrorOn(GlobalErrorType.USER_HAS_NO_ACCOUNT));
    }
  }, [hasNoCompany]);

  // Show loader while authenticating or initializing
  if (isLoading || !isAuthenticated || !isInitialized) {
    return (
      <Center pt="xxl">
        <Loader size={'xxl'} />
      </Center>
    );
  }

  return <Route {...props} />;
};

export default AuthenticatedRoute;
